/**
 * The help registry: `docs/help/**` loaded at build time into one typed table (HELP_SYSTEM_SPEC §1).
 *
 * `<HelpSidebar>` and `<HelpBadge>` in packages/ui read from here and nowhere else, and the `/help`
 * routes on both apps render the same entries as documentation — so the badge, the sidebar and the
 * manual cannot disagree. A missing id is an error, not an empty box: `getHelp` throws, and
 * `pnpm oc:lint` fails on any badge id in `apps/` with no entry behind it.
 */
import registry from "../generated/registry.json" with { type: "json" };
import { renderMarkdown } from "./markdown.ts";
export { renderMarkdown, escapeHtml } from "./markdown.ts";

export type Audience = "owner" | "broker" | "team" | "agent";
export type Surface = "console" | "web" | "intake" | "result" | "report" | "concept";

export interface HelpEntry {
  id: string;
  title: string;
  /** ≤120 characters; the hover label on an `i` badge. */
  label: string;
  audience: Audience;
  surface: Surface;
  screen: string;
  related: string[];
  spec: string | null;
  status: "PROPOSED" | "ACCEPTED";
  /** Markdown body — What this is · What to do · Where the numbers come from. */
  body: string;
  path: string;
}

const ENTRIES = (registry as { entries: HelpEntry[] }).entries;
const BY_ID = new Map(ENTRIES.map((e) => [e.id, e]));

export const helpRegistry: readonly HelpEntry[] = ENTRIES;

export class HelpMissing extends Error {
  id: string;
  constructor(id: string) {
    super(`no help entry with id "${id}" — add docs/help/<surface>/<name>.md and run pnpm oc:help`);
    this.id = id;
  }
}

export function getHelp(id: string): HelpEntry {
  const e = BY_ID.get(id);
  if (e === undefined) throw new HelpMissing(id);
  return e;
}

export const hasHelp = (id: string): boolean => BY_ID.has(id);

/** The sidebar's default entry for a page: `<surface>.<screen>`. */
export function screenDefault(surface: Surface, screen: string): HelpEntry {
  return getHelp(`${surface}.${screen}`);
}

/** Related entries, resolved; an unknown related id is a registry error the generator already refused. */
export function relatedTo(id: string): HelpEntry[] {
  return getHelp(id).related.map(getHelp);
}

/** Plain word search over title, label and body, ranked title > label > body. Vector search waits on E9. */
export function searchHelp(query: string, limit = 8, audiences?: Audience[]): HelpEntry[] {
  const q = query.trim().toLowerCase();
  if (q === "") return [];
  const words = q.split(/\s+/);
  const scored = ENTRIES
    .filter((e) => audiences === undefined || audiences.includes(e.audience))
    .map((e) => {
      const t = e.title.toLowerCase(), l = e.label.toLowerCase(), b = e.body.toLowerCase();
      let score = 0;
      for (const w of words) {
        if (t.includes(w)) score += 5;
        if (l.includes(w)) score += 3;
        if (b.includes(w)) score += 1;
      }
      return { e, score };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || a.e.id.localeCompare(b.e.id));
  return scored.slice(0, limit).map((s) => s.e);
}

/** Entries grouped for the documentation routes: surface → screen → entries. */
export function bySurface(audiences?: Audience[]): Record<string, Record<string, HelpEntry[]>> {
  const out: Record<string, Record<string, HelpEntry[]>> = {};
  for (const e of ENTRIES) {
    if (audiences !== undefined && !audiences.includes(e.audience)) continue;
    ((out[e.surface] ??= {})[e.screen] ??= []).push(e);
  }
  return out;
}

export const bodyHtml = (entry: HelpEntry): string => renderMarkdown(entry.body);

/** What a public page may show: owner and broker entries. The team's stay in the console. */
export const PUBLIC_AUDIENCES: Audience[] = ["owner", "broker"];
